import {
	clearStations,
	getImagesFromStation,
	getSensorGroupsFromStation,
	getSensorsFromStation,
	getSingleStationById,
	upsertLiftStation,
} from '@Actions/stations.action';
import RenderComponentOrLoading from '@Components/RenderComponentOrLoading';
import UpsertLiftStationForm from '@Components/UpsertLiftStationForm';
import {
	Button,
	DeleteIcon,
	EMPTY,
	PageWrapper,
	STATUS_ENUM,
	SplitButton,
	useToggle,
} from '@innovyze/stylovyze';
import { getValue } from '@Selectors';
import { useGlobalization } from '@Translations/useGlobalization';
import { DEFAULT_FACILITY_TYPE, LiftStationFormFields } from '@Types';
import { MAIN_ROUTES } from '@Types/routes';
import { parseLocationToString } from '@Utils/parseLocation';
import { FormikProps } from 'formik';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import DeleteLiftStationModal from './DeleteLiftStationModal';
import {
	selectSharedSensorGroupsStatus,
	selectSharedSensorsInitialized,
} from '@Selectors/stylovyzeStore.selectors';

export default function UpdateLiftStation(): React.ReactElement {
	const { t } = useGlobalization();
	const dispatch = useDispatch();
	const history = useHistory();
	const formRef = useRef<FormikProps<LiftStationFormFields>>();
	const [isDeleteModalOpen, toggleDeleteModalOpen] = useToggle(false);
	const [isValid, setIsValid] = useState(false);
	const [stationName, setStationName] = useState('');
	const { liftStationId } = useParams<{ liftStationId: string }>();
	const { station, initialized: stationsInitialized } =
		useSelector(getValue).stations.stationById;
	const { sensorGroups, initialized: sensorGroupInitialized } =
		useSelector(getValue).stations.sensorGroupsByStationId;
	const { sensors, initialized: sensorsInitialized } =
		useSelector(getValue).stations.sensorsByStationId;
	const { images, initialized: imagesInitialized } =
		useSelector(getValue).stations.imagesByStationId;

	const sharedSensorsInitialized = useSelector(
		selectSharedSensorsInitialized,
	);

	const sharedSensorGroupsStatus = useSelector(
		selectSharedSensorGroupsStatus,
	);

	const {
		upsertLiftStation: { status },
	} = useSelector(getValue).stations;

	useEffect(() => {
		if (liftStationId) {
			dispatch(clearStations());
			dispatch(getSingleStationById(liftStationId));
			dispatch(getSensorsFromStation(liftStationId));
			dispatch(getSensorGroupsFromStation(liftStationId));
			dispatch(getImagesFromStation(liftStationId));
		}
	}, [dispatch, liftStationId]);

	const initialized =
		stationsInitialized &&
		sensorsInitialized &&
		sensorGroupInitialized &&
		imagesInitialized;

	const isSaveButtonReady =
		sharedSensorsInitialized === true &&
		sharedSensorGroupsStatus === STATUS_ENUM.SUCCESS;

	const initialStationValues: LiftStationFormFields = React.useMemo(() => {
		if (!initialized) return {} as LiftStationFormFields;
		const stationData = station && {
			_id: station._id,
			name: station.name,
			type: station.type ?? DEFAULT_FACILITY_TYPE,
			location: parseLocationToString(station?.liftStationData?.location),
			calculatedVolumeSensor:
				station?.liftStationData?.calculatedVolumeSensor,
			outflowPipeDiameter: station?.liftStationData?.outflowPipeDiameter,
			wellThreshold: station?.liftStationData?.wellThreshold,
		};
		const sensorGroupsData =
			sensorGroups &&
			({
				sensorGroups: sensorGroups.map(
					({ sensorGroupId }) => sensorGroupId,
				),
			} as Partial<LiftStationFormFields>);
		const sensorsData =
			sensors &&
			({
				sensors: sensors.map(({ sensorId }) => sensorId),
			} as Partial<LiftStationFormFields>);

		return {
			...stationData,
			...sensorGroupsData,
			...sensorsData,
			images: images ?? [],
		} as LiftStationFormFields;
	}, [initialized]);

	const handleSubmit = (values: LiftStationFormFields) => {
		const originalSensors =
			sensors?.map(sensor => sensor.sensorId as string) || [];
		const originalSensorGroups =
			sensorGroups?.map(sensorGroup => sensorGroup.sensorGroupId) || [];
		dispatch(
			upsertLiftStation({
				...values,
				originalSensorGroups,
				originalSensors,
			}),
		);
	};

	return (
		<RenderComponentOrLoading loading={!initialized}>
			<PageWrapper
				title={stationName || EMPTY}
				secondary={
					<div style={{ display: 'flex' }}>
						<DeleteLiftStationModal
							open={isDeleteModalOpen}
							onCancel={() => toggleDeleteModalOpen()}
							formRef={formRef}
						/>
						<Button
							dataCy="cancelLiftStation"
							style={{ marginRight: 8 }}
							variant="contained"
							color="secondary"
							onClick={() => {
								history.push(MAIN_ROUTES.Stations);
							}}>
							{t('Cancel')}
						</Button>
						<SplitButton
							includeSelectedInMenu={false}
							oneClickMenu
							disabled={status === 'pending'}
							options={[
								{
									cy: 'saveLiftStation',
									onClick: () =>
										formRef?.current?.submitForm(),
									title: t('Save'),
									disabled: !isValid || !isSaveButtonReady,
								},
								{
									icon: (
										<DeleteIcon data-cy="deleteStation" />
									),
									cy: 'deleteStation',
									onClick: () => {
										toggleDeleteModalOpen();
									},
									title: t('Delete Facility'),
								},
							]}
							selectedIcon
						/>
					</div>
				}
				applyPadding
				headerUnderline>
				{handleSubmit && (
					<UpsertLiftStationForm
						onSubmit={handleSubmit}
						ref={formRef}
						initialValues={initialStationValues}
						isValid={isValid}
						setIsValid={setIsValid}
						stationName={stationName}
						setStationName={setStationName}
					/>
				)}
			</PageWrapper>
		</RenderComponentOrLoading>
	);
}
